import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Activity,
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  Lock,
  RefreshCcw,
  Sparkles,
  Zap,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/apiClient";
import LoadingSpinner from "./LoadingSpinner";

// Display data for the purchasable tiers. Prices mirror the backend PLAN_CONFIG
// (single source of truth lives in the backend /pricing endpoint).
const PLAN_DETAILS = {
  visible: {
    name: "Visible",
    target: "Independent hotels making their first move on AI search",
    priceMonthly: 39,
    priceAnnual: 32,
    audits: "Unlimited manual audits",
    features: [
      "Full issue list + step-by-step fixes",
      "15 prompt tests/mo on ChatGPT + Gemini",
      "Dual-mode SEO strategy slider",
      "3 personas analyzed",
      "Basic schema markup generator",
    ],
    gradient: "from-primary to-primary-container",
    glowColor: "shadow-primary/15",
    ringColor: "ring-primary/20",
  },
  optimized: {
    name: "Optimized",
    target: "Boutique hotels, small portfolios, and serious campaigns",
    priceMonthly: 129,
    priceAnnual: 107,
    audits: "Unlimited audits + monthly auto-scans",
    features: [
      "40 prompt tests/mo across all 3 engines",
      "All 8 personas + score breakdowns",
      "OTA vs Direct link analysis",
      'AI-generated "Suggested Changes"',
      "Monthly automated email reports",
    ],
    gradient: "from-primary to-primary-container",
    glowColor: "shadow-primary/15",
    ringColor: "ring-primary/20",
  },
  dominant: {
    name: "Dominant",
    target: "Hotel groups, resort portfolios, and multi-client agencies",
    priceMonthly: 249,
    priceAnnual: 207,
    audits: "Unlimited audits + weekly auto-scans",
    features: [
      "120 prompt tests/mo + cross-engine",
      "White-labelled PDF reports",
      "Agency seat sharing (5 users)",
      "Read-only API access",
      "Certified Partner program access",
    ],
    gradient: "from-primary to-primary-container",
    glowColor: "shadow-primary/15",
    ringColor: "ring-primary/20",
  },
};

// Display names for add-ons. Mirrors the backend's ADDONS_CONTENT
// (src/core/pricing_content.py) — the /payment/addon-status endpoint only
// returns the key/status/cadence, not display copy, so we keep a small
// lookup here (same pattern as PLAN_DETAILS above).
const ADDON_NAMES = {
  white_glove_onboarding: "White-glove onboarding",
  ai_optimized_website_build: "AI-optimized website build",
  schema_markup_implementation: "Schema markup implementation",
  extra_property: "Extra property",
  done_for_you_strategy: "Done-for-you strategy",
  strategy_session: "Strategy session",
};

/* ------------------------------------------------------------------ */
/*  Step Progress Bar                                                  */
/* ------------------------------------------------------------------ */
const STEPS = [
  { key: "review", label: "Review Plan", icon: Sparkles },
  { key: "payment", label: "Payment", icon: CreditCard },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
];

const stepIndex = (step) => {
  if (step === "review") return 0;
  if (step === "redirecting" || step === "verifying" || step === "error") return 1;
  if (step === "success") return 2;
  return 0;
};

const StepProgressBar = ({ step }) => {
  const current = stepIndex(step);

  return (
    <div className="flex items-center justify-center gap-2 mb-12">
      {STEPS.map((s, i) => {
        const isActive = i === current;
        const isCompleted = i < current;
        const Icon = s.icon;

        return (
          <React.Fragment key={s.key}>
            {i > 0 && (
              <div
                className={`w-10 h-px transition-colors duration-500 ${
                  isCompleted ? "bg-emerald-500" : "bg-surface-container-high"
                }`}
              />
            )}
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-500 ${
                isActive
                  ? "bg-primary/20 text-primary ring-1 ring-primary/30"
                  : isCompleted
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "text-on-surface-variant"
              }`}
            >
              {isCompleted ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <Icon className={`w-4 h-4 ${isActive ? "text-primary" : ""}`} />
              )}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Verifying Progress Steps                                           */
/* ------------------------------------------------------------------ */
const VerifyingSteps = ({ pollCount }) => {
  const steps = [
    { label: "Payment received", done: true },
    { label: "Activating your plan", done: pollCount > 3 },
    { label: "Unlocking features", done: pollCount > 6 },
  ];

  return (
    <div className="text-center py-4 space-y-5">
      <LoadingSpinner size="md" message="Processing your payment..." />
      <div className="space-y-3 text-left max-w-[200px] mx-auto">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            {s.done ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : i === steps.findIndex((x) => !x.done) ? (
              <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border border-surface-container-high shrink-0" />
            )}
            <span className={s.done ? "text-emerald-400" : "text-on-surface-variant"}>
              {s.label}
              {!s.done && i === steps.findIndex((x) => !x.done) ? "..." : ""}
            </span>
          </div>
        ))}
      </div>
      <p className="text-on-surface-variant text-xs">
        We&apos;re polishing your dashboard and unlocking your tools. This usually takes a few
        seconds.
      </p>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Animated Success State                                             */
/* ------------------------------------------------------------------ */
const SuccessState = ({ planInfo }) => (
  <div className="text-center py-4 animate-fadeIn">
    <div className="relative w-20 h-20 mx-auto mb-5">
      <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
      <div className="relative w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center animate-scaleIn">
        <CheckCircle2 className="w-10 h-10 text-emerald-500" />
      </div>
    </div>

    <div className="flex items-center justify-center gap-2 text-on-surface font-bold text-xl mb-1">
      <Sparkles className="w-5 h-5 text-amber-300" />
      <p>Congrats, you&apos;re all set!</p>
      <Sparkles className="w-5 h-5 text-amber-300" />
    </div>
    <p className="text-on-surface-variant text-sm mb-5">
      Your <span className="text-on-surface font-medium">{planInfo.name}</span> plan is live, your
      features are unlocked, and we&apos;re taking you to the dashboard now.
    </p>

    <div className="bg-surface-container-low rounded-xl p-4 text-left space-y-2 mb-4 border border-surface-container-high">
      <p className="text-xs text-on-surface-variant uppercase tracking-wider font-semibold mb-2">
        Unlocked features
      </p>
      {planInfo.features.slice(0, 4).map((f, i) => (
        <div key={i} className="flex items-center gap-2 text-sm text-emerald-400">
          <Zap className="w-3.5 h-3.5 shrink-0" />
          {f}
        </div>
      ))}
    </div>

    <LoadingSpinner size="md" message="Redirecting to your dashboard..." />
  </div>
);

/* ------------------------------------------------------------------ */
/*  Trust Badges                                                       */
/* ------------------------------------------------------------------ */
const TrustBadges = ({ cycle }) => (
  <div className="space-y-4 pt-2">
    <div className="flex flex-wrap gap-2 justify-center">
      {[
        { icon: Lock, label: "256-bit encryption" },
        { icon: BadgeCheck, label: "30-day money-back" },
        { icon: RefreshCcw, label: "Cancel anytime" },
      ].map((badge, i) => (
        <div
          key={i}
          className="flex items-center gap-1.5 text-[11px] text-on-surface-variant bg-surface-container-low border border-surface-container-high rounded-full px-3 py-1.5"
        >
          <badge.icon className="w-3 h-3 text-on-surface-variant" />
          {badge.label}
        </div>
      ))}
    </div>
    <p className="text-[10px] text-on-surface-variant text-center leading-relaxed">
      {cycle === "annual" ? "Billed annually." : "Billed monthly."} Cancel anytime from your account
      settings.
    </p>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main Checkout Component                                            */
/* ------------------------------------------------------------------ */
const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const addonKey = searchParams.get("addon");
  const addonQuantity = parseInt(searchParams.get("quantity") || "1", 10);
  const isAddonFlow = Boolean(addonKey);
  const [cycle, setCycle] = useState(() =>
    searchParams.get("cycle") === "annual" ? "annual" : "monthly",
  );
  const handleCycleChange = (newCycle) => {
    setCycle(newCycle);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("cycle", newCycle);
        return next;
      },
      { replace: true },
    );
  };
  const basePlan = PLAN_DETAILS[plan];
  // Stripe charges the full year upfront for annual billing (see backend
  // plan_charge_amount: amount_annual_monthly_cents * 12), so the amount due
  // at checkout must be the annual total, not the per-month-equivalent rate.
  const annualSavings = basePlan ? (basePlan.priceMonthly - basePlan.priceAnnual) * 12 : 0;
  const planInfo = basePlan
    ? {
        ...basePlan,
        price: `$${cycle === "annual" ? basePlan.priceAnnual * 12 : basePlan.priceMonthly}.00`,
        billingLabel: cycle === "annual" ? "Annual" : "Monthly",
        frequency: cycle === "annual" ? "billed annually" : "per month",
        monthlyEquivalent: cycle === "annual" ? `$${basePlan.priceAnnual}/mo` : null,
        annualSavings: cycle === "annual" ? `$${annualSavings}` : null,
      }
    : undefined;

  // Check if user is returning from Stripe (status param in URL)
  const returnStatus = searchParams.get("status");

  const { user, businessProfile, logout } = useAuth();
  const [step, setStep] = useState("review");
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addonStatusInfo, setAddonStatusInfo] = useState(null);

  useEffect(() => {
    if (user === null) navigate("/pricing");
  }, [user, navigate]);

  // Handle return from Stripe checkout — subscription plan flow only.
  useEffect(() => {
    if (isAddonFlow) return;

    // Poll payment status after redirect return
    const pollForPayment = async () => {
      try {
        const maxRetries = 15;
        let isConfirmed = false;
        let verifyRes = null;

        for (let i = 0; i < maxRetries; i++) {
          setPollCount(i + 1);
          verifyRes = await api.get("/payment/status");

          if (verifyRes.data.subscription_plan === plan) {
            isConfirmed = true;
            break;
          }

          if (i < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }

        if (isConfirmed && verifyRes) {
          setStep("success");
          // The access token only refreshes on login, so send the user back
          // through login to pick up their new plan.
          setTimeout(() => navigate("/"), 1200);
        } else {
          setStep("error");
          setError(
            "Don't worry - we've received your payment. We're still updating your account access, which can take a little longer than usual.",
          );
        }
      } catch (err) {
        console.error("Error verifying payment:", err);
        setStep("error");
        setError(
          "Your payment appears to have gone through. We're still syncing your access in the background. Please refresh shortly.",
        );
      }
    };

    if (returnStatus === "success" && user) {
      // User returned from Stripe after successful payment — poll for webhook confirmation
      setStep("verifying");
      pollForPayment();
    } else if (returnStatus === "cancel") {
      setStep("review");
    } else if (returnStatus === "failure") {
      setStep("error");
      setError("Payment was not completed. Please try again.");
    }
  }, [returnStatus, user, navigate, plan, isAddonFlow, logout]);

  // Handle return from Stripe checkout — add-on purchase flow only. Simpler than the
  // plan flow above: no JWT refresh (add-on purchases don't change subscription claims),
  // just poll /payment/addon-status until this specific add-on leaves "pending".
  useEffect(() => {
    if (!isAddonFlow) return;

    if (returnStatus === "cancel") {
      setStep("review");
      return;
    }
    if (returnStatus === "failure") {
      setStep("error");
      setError("Payment was not completed. Please try again.");
      return;
    }
    if (returnStatus !== "success" || !user) return;

    let cancelled = false;

    const pollForAddon = async () => {
      setStep("verifying");
      const maxRetries = 15;
      let finalPurchase = null;

      try {
        for (let i = 0; i < maxRetries; i++) {
          if (cancelled) return;
          setPollCount(i + 1);
          const res = await api.get("/payment/addon-status");
          const match = (res.data?.addons || []).find((a) => a.addon_key === addonKey);

          if (match && match.status !== "pending") {
            finalPurchase = match;
            break;
          }

          if (i < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }

        if (cancelled) return;

        const purchased =
          finalPurchase && (finalPurchase.status === "paid" || finalPurchase.status === "active");
        if (purchased) {
          setAddonStatusInfo(finalPurchase);
          setStep("success");
        } else {
          setStep("error");
          setError(
            "Don't worry - we've received your payment. We're still confirming your add-on purchase, which can take a little longer than usual.",
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error verifying add-on purchase:", err);
        setStep("error");
        setError(
          "Your payment appears to have gone through. We're still syncing your purchase in the background. Please refresh shortly.",
        );
      }
    };

    pollForAddon();
    return () => {
      cancelled = true;
    };
  }, [isAddonFlow, returnStatus, user, addonKey]);

  // Handle pay button click — create order and redirect to Stripe
  const handlePay = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post("/payment/create-order", { plan, billing_cycle: cycle });
      const { checkout_url } = response.data;

      if (checkout_url) {
        // Redirect to Stripe's hosted checkout page
        window.location.href = checkout_url;
      } else {
        throw new Error("No checkout URL received from payment provider");
      }
    } catch (err) {
      setLoading(false);
      const detail = err?.response?.data?.detail || err.message || "Something went wrong";
      setError(`Error: ${detail}`);
    }
  };

  if (isAddonFlow) {
    const addonName = ADDON_NAMES[addonKey] || addonKey;

    return (
      <div className="min-h-screen pt-28 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate("/pricing")}
            className="flex items-center gap-2 text-on-surface-variant hover:text-on-surface transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Pricing
          </button>

          <StepProgressBar step={step} />

          <div className="p-8 bg-surface-container-lowest border border-surface-container-high shadow-cloud rounded-3xl">
            <h1 className="text-2xl font-bold text-on-surface mb-1">{addonName}</h1>
            <p className="text-on-surface-variant text-sm mb-6">Add-on purchase</p>

            {step === "verifying" && <VerifyingSteps pollCount={pollCount} />}

            {step === "success" && (
              <div className="text-center py-4 animate-fadeIn">
                <div className="relative w-20 h-20 mx-auto mb-5">
                  <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
                  <div className="relative w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center animate-scaleIn">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 text-on-surface font-bold text-xl mb-1">
                  <Sparkles className="w-5 h-5 text-amber-300" />
                  <p>You&apos;re all set!</p>
                  <Sparkles className="w-5 h-5 text-amber-300" />
                </div>
                <p className="text-on-surface-variant text-sm mb-6">
                  <span className="text-on-surface font-medium">
                    {addonQuantity > 1 ? `${addonQuantity}× ` : ""}
                    {addonName}
                  </span>
                  {addonStatusInfo?.cadence === "monthly"
                    ? " is now active on your account."
                    : " has been purchased."}
                </p>
                <button
                  onClick={() => navigate("/pricing")}
                  className="py-3 px-6 rounded-xl font-bold text-sm bg-gradient-to-br from-primary to-primary-container text-on-primary hover:opacity-90 transition-all"
                >
                  Back to Pricing
                </button>
              </div>
            )}

            {step === "error" && (
              <div className="space-y-4">
                <div className="p-4 bg-error-container border border-error/20 rounded-xl text-error text-sm">
                  {error}
                </div>
                <button
                  onClick={() => navigate("/pricing")}
                  className="w-full py-3 bg-surface-container-low hover:bg-surface-container-high text-on-surface rounded-xl font-medium transition-colors"
                >
                  Back to Pricing
                </button>
              </div>
            )}

            {step === "review" && (
              <div className="space-y-4">
                <p className="text-on-surface-variant text-sm">
                  {returnStatus === "cancel"
                    ? "Checkout was cancelled — no charge was made."
                    : "There's no add-on checkout in progress. Head back to pricing to purchase an add-on."}
                </p>
                <button
                  onClick={() => navigate("/pricing")}
                  className="w-full py-3 bg-surface-container-low hover:bg-surface-container-high text-on-surface rounded-xl font-medium transition-colors"
                >
                  Back to Pricing
                </button>
              </div>
            )}
          </div>
        </div>

        {/* CSS animations (shared with the plan success state) */}
        <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes scaleIn {
                        from { opacity: 0; transform: scale(0.5); }
                        to { opacity: 1; transform: scale(1); }
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.6s ease-out;
                    }
                    .animate-scaleIn {
                        animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                    }
                `}</style>
      </div>
    );
  }

  if (!plan || !planInfo) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-on-surface-variant text-xl mb-4">Invalid plan selected.</p>
          <button
            onClick={() => navigate("/pricing")}
            className="text-primary hover:opacity-80 underline"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate("/pricing")}
          className="flex items-center gap-2 text-on-surface-variant hover:text-on-surface transition-colors mb-6 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Pricing
        </button>

        {/* Step Progress Bar */}
        <StepProgressBar step={step} />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left: Order Summary */}
          <div className="lg:col-span-3 space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-on-surface mb-2">Checkout</h1>
              <p className="text-on-surface-variant text-lg">
                Complete your subscription to unlock premium features.
              </p>
            </div>

            {/* Plan card with glow */}
            <div
              className={`p-8 rounded-3xl bg-gradient-to-br ${planInfo.gradient} border border-white/10 ring-1 ${planInfo.ringColor} shadow-xl ${planInfo.glowColor} hover:shadow-2xl transition-shadow duration-500`}
            >
              <div className="flex flex-wrap justify-between items-start gap-x-4 gap-y-2 mb-6">
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold text-white">{planInfo.name}</h2>
                  <p className="text-white/60 mt-1">{planInfo.target}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-4xl font-extrabold text-white tracking-tight whitespace-nowrap">
                    {planInfo.price}
                  </div>
                  <div className="text-white/50 text-sm whitespace-nowrap">
                    {planInfo.frequency}
                  </div>
                  {cycle === "annual" && (
                    <div className="text-emerald-300 text-sm font-semibold mt-1 whitespace-nowrap">
                      {planInfo.monthlyEquivalent} · Save {planInfo.annualSavings}/yr
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm font-semibold text-white/80 mb-6 bg-white/10 p-3 rounded-xl">
                <Activity className="w-4 h-4" /> {planInfo.audits}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {planInfo.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>

            {/* Customer info */}
            {user && (
              <div className="p-6 rounded-2xl bg-surface-container-low border border-surface-container-high">
                <h3 className="text-lg font-semibold text-on-surface mb-4">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-on-surface-variant block mb-1">Name</span>
                    <span className="text-on-surface font-medium">{user.name}</span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant block mb-1">Email</span>
                    <span className="text-on-surface font-medium">{user.email}</span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant block mb-1">Company</span>
                    <span className="text-on-surface font-medium">
                      {businessProfile?.business_name || "—"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Payment panel */}
          <div className="lg:col-span-2">
            <div className="sticky top-32 p-8 bg-surface-container-lowest border border-surface-container-high shadow-cloud rounded-3xl space-y-6">
              <h3 className="text-xl font-bold text-on-surface flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-primary" /> Payment
              </h3>

              {/* Order total */}
              <div className="space-y-3 pb-6 border-b border-surface-container-high">
                <div className="flex justify-between text-sm">
                  <span className="text-on-surface-variant">{planInfo.name}</span>
                  <span className="text-on-surface">{planInfo.price}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-on-surface-variant">Billing period</span>
                  <span className="text-on-surface">{planInfo.billingLabel}</span>
                </div>
                {cycle === "annual" && (
                  <div className="flex justify-between text-sm">
                    <span className="text-on-surface-variant">You save</span>
                    <span className="text-emerald-500 font-medium">
                      {planInfo.annualSavings}/yr ({planInfo.monthlyEquivalent})
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-3 border-t border-surface-container-high">
                  <span className="text-on-surface">Total</span>
                  <span className="text-on-surface">{planInfo.price}</span>
                </div>
              </div>

              {/* Review state — pay button */}
              {step === "review" && (
                <div className="space-y-5">
                  {/* Billing cycle toggle — subscription plans only, not add-ons */}
                  {basePlan && (
                    <div className="flex items-center justify-center">
                      <div className="inline-flex items-center bg-surface-container-high rounded-full p-1">
                        {[
                          { key: "monthly", label: "Monthly", price: basePlan.priceMonthly },
                          { key: "annual", label: "Annual", price: basePlan.priceAnnual },
                        ].map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => handleCycleChange(option.key)}
                            className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                              cycle === option.key
                                ? "bg-surface-container-lowest text-primary shadow-sm"
                                : "text-on-surface-variant hover:text-on-surface"
                            }`}
                          >
                            {option.label}{" "}
                            <span className="font-normal opacity-70">${option.price}/mo</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handlePay}
                    disabled={loading}
                    className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 active:scale-[0.98] ${
                      loading
                        ? "bg-primary/50 text-on-primary/50 cursor-not-allowed"
                        : "bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-xl shadow-primary/20 hover:opacity-90"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Redirecting to payment...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" /> Pay {planInfo.price}
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-center gap-1.5 text-xs text-on-surface-variant">
                    <ExternalLink className="w-3 h-3" />
                    You'll be redirected to Stripe's secure checkout
                  </div>

                  <TrustBadges cycle={cycle} />
                </div>
              )}

              {/* Redirecting state */}
              {step === "redirecting" && (
                <div className="text-center py-4">
                  <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
                  <p className="text-on-surface font-medium mb-1">Redirecting to Stripe...</p>
                  <p className="text-on-surface-variant text-sm">
                    You'll complete payment on Stripe's secure page.
                  </p>
                </div>
              )}

              {/* Verifying state */}
              {step === "verifying" && <VerifyingSteps pollCount={pollCount} />}

              {/* Success state */}
              {step === "success" && <SuccessState planInfo={planInfo} />}

              {/* Error state */}
              {step === "error" && (
                <div className="space-y-4">
                  <div className="p-4 bg-error-container border border-error/20 rounded-xl text-error text-sm">
                    {error}
                  </div>
                  <button
                    onClick={() => {
                      setStep("review");
                      setError(null);
                      // Clean URL params
                      navigate(`/checkout?plan=${plan}&cycle=${cycle}`, { replace: true });
                    }}
                    className="w-full py-3 bg-surface-container-low hover:bg-surface-container-high text-on-surface rounded-xl font-medium transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.5); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.6s ease-out;
                }
                .animate-scaleIn {
                    animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
            `}</style>
    </div>
  );
};

export default Checkout;
